/** The saved presentation contract is shared with Asset and DSPy. These
 * directions refer only to the Story's existing evidence; they add no media,
 * coordinates, executable styling or authority to connect a route. */
export const STORY_COMPOSITION_THEME_REQUIRED_KEYS = [
  'name',
  'mode',
  'accent',
  'land',
  'water',
  'typography',
] as const;
export const STORY_COMPOSITION_THEME_OPTIONAL_COLOR_KEYS = ['park', 'road', 'building', 'label'] as const;

export interface BrainTeamStoryCompositionTheme {
  name: string;
  mode: 'light' | 'dark';
  accent: string;
  land: string;
  water: string;
  park?: string;
  road?: string;
  building?: string;
  label?: string;
  typography: 'sans' | 'serif';
}

export interface BrainTeamStoryCompositionScene {
  target_id: string;
  focus_target_ids: string[];
  media_layout: 'hero' | 'collage' | 'compare';
  map_context: 'all' | 'scene';
  image_scale: number;
}

export interface BrainTeamStoryComposition {
  schema_version: 1;
  theme: BrainTeamStoryCompositionTheme;
  scenes: BrainTeamStoryCompositionScene[];
}

const TARGET = /^reg:[a-f0-9]{24}:[a-f0-9]{24}$/;
const COLOR = /^#[a-fA-F0-9]{6}$/;
const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);
const exactKeys = (value: Record<string, unknown>, keys: readonly string[]) =>
  Object.keys(value).length === keys.length &&
  keys.every((key) => Object.prototype.hasOwnProperty.call(value, key));

/** Missing is legacy; present malformed is rejected by each caller. Summary
 * reads can validate their declared set; playable/detail reads supply the
 * exact separately authorized story targets. No coercion or repair occurs. */
export function parseBrainTeamStoryComposition(
  value: unknown,
  targetIds?: readonly string[],
): BrainTeamStoryComposition | undefined {
  if (
    !isRecord(value) ||
    !exactKeys(value, ['schema_version', 'theme', 'scenes']) ||
    value.schema_version !== 1 ||
    !isRecord(value.theme) ||
    !Array.isArray(value.scenes) ||
    value.scenes.length < 2 ||
    value.scenes.length > 96
  )
    return undefined;
  const theme = value.theme;
  const allowedThemeKeys = new Set<string>([
    ...STORY_COMPOSITION_THEME_REQUIRED_KEYS,
    ...STORY_COMPOSITION_THEME_OPTIONAL_COLOR_KEYS,
  ]);
  if (
    !STORY_COMPOSITION_THEME_REQUIRED_KEYS.every((key) => Object.prototype.hasOwnProperty.call(theme, key)) ||
    Object.keys(theme).some((key) => !allowedThemeKeys.has(key)) ||
    typeof theme.name !== 'string' ||
    !theme.name.trim() ||
    theme.name !== theme.name.trim() ||
    theme.name.length > 80 ||
    (theme.mode !== 'light' && theme.mode !== 'dark') ||
    (theme.typography !== 'sans' && theme.typography !== 'serif') ||
    [theme.accent, theme.land, theme.water].some(
      (color) => typeof color !== 'string' || !COLOR.test(color),
    ) ||
    STORY_COMPOSITION_THEME_OPTIONAL_COLOR_KEYS.some(
      (key) =>
        Object.prototype.hasOwnProperty.call(theme, key) &&
        (typeof theme[key] !== 'string' || !COLOR.test(theme[key] as string)),
    )
  )
    return undefined;
  const scenes: BrainTeamStoryCompositionScene[] = [];
  for (const scene of value.scenes) {
    if (
      !isRecord(scene) ||
      !exactKeys(scene, ['target_id', 'focus_target_ids', 'media_layout', 'map_context', 'image_scale']) ||
      typeof scene.target_id !== 'string' ||
      !TARGET.test(scene.target_id) ||
      !Array.isArray(scene.focus_target_ids) ||
      scene.focus_target_ids.length < 1 ||
      scene.focus_target_ids.length > 6 ||
      new Set(scene.focus_target_ids).size !== scene.focus_target_ids.length ||
      scene.focus_target_ids.some((target) => typeof target !== 'string' || !TARGET.test(target)) ||
      (scene.media_layout !== 'hero' &&
        scene.media_layout !== 'collage' &&
        scene.media_layout !== 'compare') ||
      (scene.map_context !== 'all' && scene.map_context !== 'scene') ||
      typeof scene.image_scale !== 'number' ||
      !Number.isFinite(scene.image_scale) ||
      scene.image_scale < 0.65 ||
      scene.image_scale > 1.35
    )
      return undefined;
    scenes.push({
      target_id: scene.target_id,
      focus_target_ids: [...scene.focus_target_ids] as string[],
      media_layout: scene.media_layout,
      map_context: scene.map_context,
      image_scale: scene.image_scale,
    });
  }
  const declared = new Set(scenes.map((scene) => scene.target_id));
  const authorized = new Set(targetIds ?? declared);
  if (
    declared.size !== scenes.length ||
    authorized.size !== scenes.length ||
    (targetIds && authorized.size !== targetIds.length) ||
    scenes.some(
      (scene) =>
        !authorized.has(scene.target_id) || scene.focus_target_ids.some((target) => !authorized.has(target)),
    )
  )
    return undefined;
  const byTarget = new Map(scenes.map((scene) => [scene.target_id, scene] as const));
  return {
    schema_version: 1,
    theme: {
      name: theme.name,
      mode: theme.mode,
      typography: theme.typography,
      accent: theme.accent as string,
      land: theme.land as string,
      water: theme.water as string,
      ...(typeof theme.park === 'string' ? { park: theme.park } : {}),
      ...(typeof theme.road === 'string' ? { road: theme.road } : {}),
      ...(typeof theme.building === 'string' ? { building: theme.building } : {}),
      ...(typeof theme.label === 'string' ? { label: theme.label } : {}),
    },
    scenes: targetIds ? targetIds.map((target) => byTarget.get(target)!) : scenes,
  };
}

/** Explicit author editing operation for changed scene membership. Consumers
 * never call this to make an invalid server projection appear playable. */
export function reconcileBrainTeamStoryComposition(
  composition: BrainTeamStoryComposition | null | undefined,
  targetIds: readonly string[],
): BrainTeamStoryComposition | undefined {
  const current = parseBrainTeamStoryComposition(composition);
  if (
    !current ||
    targetIds.length < 2 ||
    targetIds.length > 96 ||
    new Set(targetIds).size !== targetIds.length ||
    targetIds.some((target) => !TARGET.test(target))
  ) {
    return undefined;
  }
  const allowed = new Set(targetIds);
  const byTarget = new Map(current.scenes.map((scene) => [scene.target_id, scene] as const));
  return {
    schema_version: 1,
    theme: { ...current.theme },
    scenes: targetIds.map((target_id): BrainTeamStoryCompositionScene => {
      const previous = byTarget.get(target_id);
      const focus = previous?.focus_target_ids.filter((target) => allowed.has(target)) ?? [];
      return {
        target_id,
        focus_target_ids: focus.length ? focus : [target_id],
        media_layout: previous?.media_layout ?? 'hero',
        map_context: previous?.map_context ?? 'all',
        image_scale: previous?.image_scale ?? 1,
      };
    }),
  };
}
